import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Send, ChevronLeft, MessageSquare, User, Search, AlertCircle, CheckCircle, Info } from "lucide-react";
import AdminSidebar from "../../components/admin/AdminSidebar";
import axios from "axios";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

export default function AdminMessages() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const chatParam = searchParams.get("chat");
  
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState("");
  const [activeChat, setActiveChat] = useState(null);
  const [user, setUser] = useState(null);
  const [msgType, setMsgType] = useState("info"); // 'info', 'error', 'success'
  
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const uStr = localStorage.getItem("user") || sessionStorage.getItem("user");
    if (uStr) {
      setUser(JSON.parse(uStr));
      fetchConversations();
    }
  }, []);

  useEffect(() => {
    if (chatParam) {
      fetchMessages(chatParam);
      const conv = conversations.find(c => c.id == chatParam);
      if (conv) setActiveChat(conv);
    } else {
      setActiveChat(null);
      setMessages([]);
    }
  }, [chatParam, conversations]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchConversations = async () => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    try {
      const res = await axios.get(`${apiUrl}/api/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(res.data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const fetchMessages = async (id) => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    try {
      const res = await axios.get(`${apiUrl}/api/messages/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(res.data);
      
      // Mark as read
      await axios.put(`${apiUrl}/api/messages/read/${id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !chatParam) return;

    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    try {
      const res = await axios.post(`${apiUrl}/api/messages`, 
        { conversationId: chatParam, content: newMessage, type: msgType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.status === 200 || res.status === 201) {
        setMessages([...messages, res.data]);
        setNewMessage("");
        setMsgType("info"); // Reset to info after sending
        fetchConversations(); // Refresh last message in list
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar />
      
      <main className="ml-64 flex-grow flex flex-col h-screen overflow-hidden">
        <header className="p-8 pb-4">
          <h1 className="text-3xl font-bold text-gray-900">Admin Inbox</h1>
          <p className="text-gray-500 text-sm">Communicate with sellers regarding their listings.</p>
        </header>

        <div className="flex-grow flex p-8 pt-0 gap-6 overflow-hidden">
          {/* CONVERSATION LIST */}
          <div className={`bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden flex flex-col w-1/3 ${activeChat ? 'hidden lg:flex' : 'flex'}`}>
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="font-bold text-gray-900">All Chats</h2>
              <div className="bg-gray-100 p-2 rounded-lg text-gray-400">
                <Search size={18} />
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-6 text-center text-gray-500 italic text-sm">Loading chats...</div>
              ) : conversations.length === 0 ? (
                <div className="p-10 text-center space-y-4">
                  <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto text-gray-300">
                    <MessageSquare size={32} />
                  </div>
                  <p className="text-gray-400 text-sm italic">No conversations found.</p>
                </div>
              ) : (
                conversations.map((conv) => {
                  const isBuyer = conv.buyer_id === user?.id;
                  const contactName = isBuyer ? conv.sellerName : conv.buyerName;
                  const contactPic = isBuyer ? conv.sellerPicture : conv.buyerPicture;
                  
                  return (
                    <button
                      key={conv.id}
                      onClick={() => setSearchParams({ chat: conv.id })}
                      className={`w-full p-4 flex items-center gap-4 transition-colors border-b border-gray-50 hover:bg-gray-50 ${chatParam == conv.id ? 'bg-blue-50/50' : ''}`}
                    >
                      <div className="relative">
                        <img 
                          src={conv.postImage || "/placeholder.jpg"} 
                          className="w-12 h-12 rounded-xl object-cover border bg-gray-50"
                          alt=""
                        />
                        <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-2 border-white bg-blue-600 flex items-center justify-center text-[10px] text-white font-bold uppercase overflow-hidden">
                          {contactPic ? <img src={contactPic} className="w-full h-full object-cover" /> : contactName[0]}
                        </div>
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <div className="flex justify-between items-baseline mb-0.5">
                          <h4 className="font-bold text-gray-900 truncate text-sm">{contactName}</h4>
                          <span className="text-[10px] text-gray-400">
                            {conv.lastMessageDate ? new Date(conv.lastMessageDate).toLocaleDateString() : ''}
                          </span>
                        </div>
                        <p className="text-[10px] text-blue-600 font-bold truncate mb-1">RE: {conv.postTitle}</p>
                        <p className="text-xs text-gray-500 truncate">{conv.lastMessage || '...'}</p>
                      </div>
                      {conv.unreadCount > 0 && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* CHAT WINDOW */}
          <div className="flex-grow bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden flex flex-col relative">
            {activeChat ? (
              <>
                {/* CHAT HEADER */}
                <div className="p-5 border-b flex items-center justify-between bg-white sticky top-0 z-10">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold">
                      { (activeChat.buyer_id === user?.id ? activeChat.sellerName : activeChat.buyerName)[0] }
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">
                        {activeChat.buyer_id === user?.id ? activeChat.sellerName : activeChat.buyerName}
                      </h3>
                      <p className="text-[11px] text-blue-600 font-bold uppercase tracking-wider">
                        Listing: {activeChat.postTitle}
                      </p>
                    </div>
                  </div>
                  <button 
                    onClick={() => navigate(`/admin/posts`)}
                    className="px-4 py-2 bg-gray-50 text-gray-600 text-xs font-bold rounded-xl hover:bg-gray-100 transition shadow-sm"
                  >
                    View Post
                  </button>
                </div>

                {/* MESSAGES AREA */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-gray-50/20">
                  {messages.map((msg) => {
                    const isSystem = msg.type !== 'info';
                    const bubbleStyle = msg.sender_id === user?.id
                      ? (msg.type === 'error' ? 'bg-red-600 text-white' : msg.type === 'success' ? 'bg-green-600 text-white' : 'bg-blue-600 text-white')
                      : 'bg-white text-gray-900 border border-gray-100';

                    return (
                      <div 
                        key={msg.id}
                        className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[70%] p-4 rounded-2xl shadow-sm ${bubbleStyle} ${msg.sender_id === user?.id ? 'rounded-tr-none' : 'rounded-tl-none'}`}>
                          <div className="flex items-start gap-2">
                            {msg.type === 'error' && <AlertCircle size={14} className="mt-0.5 shrink-0" />}
                            {msg.type === 'success' && <CheckCircle size={14} className="mt-0.5 shrink-0" />}
                            <p className="text-sm leading-relaxed">{msg.content}</p>
                          </div>
                          <span className={`text-[10px] mt-2 block opacity-60`}>
                            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* INPUT AREA */}
                <div className="px-6 py-2 border-t bg-gray-50 flex gap-4">
                   <button 
                    onClick={() => setMsgType("info")}
                    className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all ${msgType === 'info' ? 'bg-blue-600 text-white shadow-md' : 'bg-white text-gray-400 hover:bg-gray-100'}`}
                   >Standard</button>
                   <button 
                    onClick={() => setMsgType("error")}
                    className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all ${msgType === 'error' ? 'bg-red-600 text-white shadow-md' : 'bg-white text-gray-400 hover:bg-gray-100'}`}
                   >Warning / Error</button>
                   <button 
                    onClick={() => setMsgType("success")}
                    className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all ${msgType === 'success' ? 'bg-green-600 text-white shadow-md' : 'bg-white text-gray-400 hover:bg-gray-100'}`}
                   >Appreciation</button>
                </div>
                <form onSubmit={handleSendMessage} className="p-6 pt-2 bg-white flex gap-3">
                  <input 
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message to the seller..."
                    className="flex-1 px-5 py-4 rounded-2xl bg-gray-50 border-transparent focus:bg-white focus:border-blue-500 focus:ring-0 transition-all text-sm outline-none shadow-inner"
                  />
                  <button 
                    type="submit"
                    disabled={!newMessage.trim()}
                    className="w-14 h-14 bg-blue-600 text-white rounded-2xl flex items-center justify-center hover:bg-blue-700 transition disabled:opacity-50 shadow-lg shadow-blue-200"
                  >
                    <Send className="w-6 h-6" />
                  </button>
                </form>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-10 space-y-4">
                <div className="w-24 h-24 bg-blue-50 rounded-full flex items-center justify-center animate-pulse">
                  <MessageSquare className="w-10 h-10 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Select a Conversation</h3>
                <p className="text-gray-400 max-w-sm text-sm">
                  Click on a chat to view message history and send moderation instructions to the seller.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
